/**
 * GoProvalo Header
 * File: assets/goprovalo-header.js
 */

(() => {
  'use strict';

  const HEADER_SELECTOR = '[data-gp-header]';
  const OPEN_CLASS = 'is-open';

  /**
   * Normalize values used in property searches.
   *
   * @param {string} value
   * @returns {string}
   */
  function normalizeSearchValue(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Get elements that can receive keyboard focus.
   *
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getFocusableElements(container) {
    if (!container) {
      return [];
    }

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter(element => {
      return !element.hasAttribute('hidden');
    });
  }

  /**
   * Initialize sticky-header visual state.
   *
   * Sticky positioning is controlled by CSS on #header-group.
   * JavaScript only adds the compact/shadow state.
   *
   * @param {HTMLElement} header
   */
  function initializeScrollState(header) {
    if (header.dataset.gpScrollReady === 'true') {
      return;
    }

    header.dataset.gpScrollReady = 'true';

    const headerGroup =
      document.getElementById('header-group');

    let ticking = false;

    const getScrollTop = () => {
      return Math.max(
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
    };

    const update = () => {
      const isScrolled = getScrollTop() > 20;

      header.classList.toggle(
        'is-scrolled',
        isScrolled
      );

      headerGroup?.classList.toggle(
        'gp-header-group--scrolled',
        isScrolled
      );

      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();

    window.addEventListener(
      'scroll',
      requestUpdate,
      { passive: true }
    );

    document.addEventListener(
      'scroll',
      requestUpdate,
      {
        passive: true,
        capture: true
      }
    );
  }

  /**
   * Close all desktop dropdowns.
   *
   * @param {HTMLElement} header
   * @param {HTMLElement|null} except
   */
  function closeDropdowns(header, except = null) {
    header
      .querySelectorAll('[data-gp-dropdown-toggle]')
      .forEach(toggle => {
        if (toggle === except) {
          return;
        }

        const controlledId =
          toggle.getAttribute('aria-controls');

        const dropdown = controlledId
          ? document.getElementById(controlledId)
          : null;

        toggle.setAttribute(
          'aria-expanded',
          'false'
        );

        dropdown?.classList.remove(OPEN_CLASS);
      });
  }

  /**
   * Initialize desktop dropdown menus.
   *
   * @param {HTMLElement} header
   */
  function initializeDropdowns(header) {
    header
      .querySelectorAll('[data-gp-dropdown-toggle]')
      .forEach(toggle => {
        if (toggle.dataset.gpReady === 'true') {
          return;
        }

        toggle.dataset.gpReady = 'true';

        const controlledId =
          toggle.getAttribute('aria-controls');

        const dropdown = controlledId
          ? document.getElementById(controlledId)
          : null;

        if (!dropdown) {
          return;
        }

        toggle.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();

          const willOpen =
            toggle.getAttribute('aria-expanded') !==
            'true';

          closeDropdowns(
            header,
            willOpen ? toggle : null
          );

          toggle.setAttribute(
            'aria-expanded',
            String(willOpen)
          );

          dropdown.classList.toggle(
            OPEN_CLASS,
            willOpen
          );
        });

        dropdown.addEventListener(
          'click',
          event => {
            event.stopPropagation();
          }
        );
      });

    document.addEventListener('click', event => {
      if (!header.contains(event.target)) {
        closeDropdowns(header);
      }
    });
  }

  /**
   * Initialize the expandable desktop search.
   *
   * @param {HTMLElement} header
   */
  function initializeDesktopSearch(header) {
    const toggle = header.querySelector(
      '[data-gp-search-toggle]'
    );

    const panel = header.querySelector(
      '[data-gp-header-search]'
    );

    const input = header.querySelector(
      '[data-gp-header-search-input]'
    );

    const closeButton = header.querySelector(
      '[data-gp-search-close]'
    );

    const form = header.querySelector(
      '[data-gp-header-search-form]'
    );

    if (!toggle || !panel) {
      console.warn(
        'GoProvalo header search elements were not found.'
      );

      return;
    }

    if (toggle.dataset.gpReady === 'true') {
      return;
    }

    toggle.dataset.gpReady = 'true';

    const openSearch = () => {
      closeDropdowns(header);

      panel.classList.add(OPEN_CLASS);
      panel.setAttribute('aria-hidden', 'false');

      toggle.setAttribute(
        'aria-expanded',
        'true'
      );

      window.setTimeout(() => {
        input?.focus();
      }, 50);
    };

    const closeSearch = ({
      restoreFocus = true
    } = {}) => {
      panel.classList.remove(OPEN_CLASS);
      panel.setAttribute('aria-hidden', 'true');

      toggle.setAttribute(
        'aria-expanded',
        'false'
      );

      if (restoreFocus) {
        toggle.focus();
      }
    };

    toggle.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();

      const isOpen =
        panel.classList.contains(OPEN_CLASS);

      if (isOpen) {
        closeSearch();
      } else {
        openSearch();
      }
    });

    panel.addEventListener('click', event => {
      event.stopPropagation();
    });

    closeButton?.addEventListener(
      'click',
      event => {
        event.preventDefault();
        closeSearch();
      }
    );

    form?.addEventListener('submit', event => {
      if (!input) {
        return;
      }

      const value =
        normalizeSearchValue(input.value);

      if (!value) {
        event.preventDefault();
        input.focus();
        return;
      }

      input.value = value;
    });

    document.addEventListener('click', event => {
      if (
        panel.classList.contains(OPEN_CLASS) &&
        !panel.contains(event.target) &&
        !toggle.contains(event.target)
      ) {
        closeSearch({
          restoreFocus: false
        });
      }
    });

    document.addEventListener('keydown', event => {
      if (
        event.key === 'Escape' &&
        panel.classList.contains(OPEN_CLASS)
      ) {
        closeSearch();
      }
    });
  }

  /**
   * Initialize mobile submenu buttons.
   *
   * @param {HTMLElement} header
   */
  function initializeMobileSubmenus(header) {
    header
      .querySelectorAll(
        '[data-gp-mobile-submenu-toggle]'
      )
      .forEach(toggle => {
        if (toggle.dataset.gpReady === 'true') {
          return;
        }

        toggle.dataset.gpReady = 'true';

        const controlledId =
          toggle.getAttribute('aria-controls');

        const submenu = controlledId
          ? document.getElementById(controlledId)
          : null;

        if (!submenu) {
          return;
        }

        toggle.addEventListener('click', () => {
          const willOpen =
            toggle.getAttribute('aria-expanded') !==
            'true';

          toggle.setAttribute(
            'aria-expanded',
            String(willOpen)
          );

          submenu.classList.toggle(
            OPEN_CLASS,
            willOpen
          );
        });
      });
  }

  /**
   * Initialize mobile navigation drawer.
   *
   * @param {HTMLElement} header
   */
  function initializeMobileNavigation(header) {
    const toggle = header.querySelector(
      '[data-gp-mobile-toggle]'
    );

    const navigation = header.querySelector(
      '[data-gp-mobile-nav]'
    );

    const drawer = navigation?.querySelector(
      '.gp-mobile-nav__drawer'
    );

    const closeButtons =
      navigation?.querySelectorAll(
        '[data-gp-mobile-close]'
      );

    if (!toggle || !navigation || !drawer) {
      return;
    }

    if (toggle.dataset.gpReady === 'true') {
      return;
    }

    toggle.dataset.gpReady = 'true';

    let previousFocus = null;

    const openNavigation = () => {
      previousFocus = document.activeElement;

      navigation.classList.add(OPEN_CLASS);
      navigation.setAttribute(
        'aria-hidden',
        'false'
      );

      toggle.setAttribute(
        'aria-expanded',
        'true'
      );

      document.body.classList.add(
        'gp-mobile-nav-open'
      );

      window.setTimeout(() => {
        getFocusableElements(drawer)[0]?.focus();
      }, 100);
    };

    const closeNavigation = ({
      restoreFocus = true
    } = {}) => {
      navigation.classList.remove(OPEN_CLASS);
      navigation.setAttribute(
        'aria-hidden',
        'true'
      );

      toggle.setAttribute(
        'aria-expanded',
        'false'
      );

      document.body.classList.remove(
        'gp-mobile-nav-open'
      );

      if (
        restoreFocus &&
        previousFocus instanceof HTMLElement
      ) {
        previousFocus.focus();
      }
    };

    toggle.addEventListener('click', () => {
      if (
        navigation.classList.contains(OPEN_CLASS)
      ) {
        closeNavigation();
      } else {
        openNavigation();
      }
    });

    closeButtons?.forEach(button => {
      button.addEventListener('click', () => {
        closeNavigation();
      });
    });

    navigation.addEventListener(
      'keydown',
      event => {
        if (event.key === 'Escape') {
          closeNavigation();
          return;
        }

        if (event.key !== 'Tab') {
          return;
        }

        const focusable =
          getFocusableElements(drawer);

        if (!focusable.length) {
          return;
        }

        const first = focusable[0];
        const last =
          focusable[focusable.length - 1];

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    );

    window.addEventListener('resize', () => {
      if (
        window.innerWidth > 989 &&
        navigation.classList.contains(OPEN_CLASS)
      ) {
        closeNavigation({
          restoreFocus: false
        });
      }
    });
  }

  /**
   * Initialize one header.
   *
   * @param {HTMLElement} header
   */
  function initializeHeader(header) {
    if (!(header instanceof HTMLElement)) {
      return;
    }

    initializeScrollState(header);
    initializeDropdowns(header);
    initializeDesktopSearch(header);
    initializeMobileSubmenus(header);
    initializeMobileNavigation(header);

    header.dataset.gpHeaderReady = 'true';
  }

  /**
   * Initialize all GoProvalo headers.
   *
   * @param {ParentNode} context
   */
  function initializeAll(context = document) {
    context
      .querySelectorAll(HEADER_SELECTOR)
      .forEach(initializeHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll(),
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    event => {
      initializeAll(event.target);
    }
  );
})();